import React, { useCallback, useEffect, useState } from 'react';
import { Html } from '@react-three/drei';
import { useWorldStore } from '../state/worldStore';
import AnimatedResident from './AnimatedResident';
import { maybeBark } from '../services/barkEngine';

// Npc — one resident in the city-life system (Spec: two-tier NPC system).
// Wraps the rigged body with the tier plumbing: proximity drives far → mid →
// near → engaged, a floating label reflects it, and tapping an engageable NPC
// hands off to the live voice chat (Tier E).
//
// Placement editor (?place=edit): click a resident to select, arrows move it on
// X/Z, [ ] rotate; the tuned post persists per-slug in localStorage and logs a
// paste-ready line for roster.json (NpcPlacePanel is the on-screen control).

const labelStyle = (hot) => ({
  padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap',
  font: '600 12px system-ui, sans-serif',
  color: hot ? '#1c1206' : '#ffe9c4',
  background: hot ? '#ffb25e' : 'rgba(16,10,6,0.72)',
  border: '1px solid rgba(255,178,94,0.4)',
  backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
  transform: 'translateY(-6px)', pointerEvents: 'none', userSelect: 'none',
});

// Deterministic per-NPC hash → a stable idle phase offset + playback rate, so a
// crowd of the same idle clip doesn't move in lockstep (and drifts over time).
const hash = (n) => { const x = Math.sin(n * 12.9898) * 43758.5453; return x - Math.floor(x); };
// Single source of truth = roster.json (autosaved). No localStorage — mixing
// the two made sizes depend on ?fresh and diverge between returns.
const loadPos = (npc) => ({ pos: npc.post, rotY: npc.rotationY || 0, height: npc.height || 1.8 });

// Debounced autosave straight into roster.json via the dev endpoint (vite.config
// npcSavePlugin) — so placement/size edits persist to the file, no copy/paste.
const saveTimers = {};
function saveNpc(slug, { pos, rotY, height }) {
  clearTimeout(saveTimers[slug]);
  saveTimers[slug] = setTimeout(() => {
    fetch('/__npc-save', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, post: pos, rotationY: rotY, height }),
    }).catch(() => { /* dev-only; falls back to the copy button */ });
  }, 400);
}

export default function Npc({ npc }) {
  const openChat = useWorldStore((s) => s.openChat);
  const setNpcEdit = useWorldStore((s) => s.setNpcEdit);
  const selectedSlug = useWorldStore((s) => s.npcEdit.slug);
  const PLACE = useWorldStore((s) => s.adminTool) === 'place'; // placement edit mode (AdminPanel)
  const [tier, setTier] = useState('far');
  const [xf, setXf] = useState(() => loadPos(npc));
  const onTier = useCallback((t) => setTier(t), []);
  // Ambient barks (Tier A): the resident murmurs in their own voice when you
  // HOVER over them (deliberate, unlike a walk-by) — a greeting, or idle chatter
  // for variety. barkEngine cooldowns keep repeated hovers from spamming.
  const onHover = useCallback(() => {
    if (PLACE || npc.avatarId == null) return;
    maybeBark(npc.slug, Math.random() < 0.6 ? 'greeting_passerby' : 'idle_chatter');
  }, [npc.slug, npc.avatarId]);

  const seed = npc.avatarId ?? Array.from(npc.slug || 'x').reduce((a, c) => a + c.charCodeAt(0), 0);
  const phaseOffset = hash(seed + 1);
  const rate = 0.9 + hash(seed + 2) * 0.2; // 0.9–1.1×

  const engageable = npc.avatarId != null && !!npc.rigged;
  const selected = PLACE && selectedSlug === npc.slug;

  // Placement: move the selected resident with the keyboard, persist + log.
  useEffect(() => {
    if (!PLACE || !selected) return undefined;
    const MOVE = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', '[', ']', '-', '=', '+', 'u', 'j'];
    const onKey = (e) => {
      if (!MOVE.includes(e.key)) return;
      e.preventDefault();
      setXf((s) => {
        const step = e.shiftKey ? 0.2 : 1;
        let pos = [...s.pos]; let rotY = s.rotY; let height = s.height;
        if (e.key === 'ArrowLeft') pos[0] -= step; else if (e.key === 'ArrowRight') pos[0] += step;
        else if (e.key === 'ArrowUp') pos[2] -= step; else if (e.key === 'ArrowDown') pos[2] += step;
        else if (e.key === 'u') pos[1] += (e.shiftKey ? 0.05 : 0.2); else if (e.key === 'j') pos[1] -= (e.shiftKey ? 0.05 : 0.2); // raise/lower
        else if (e.key === '[') rotY -= 0.1; else if (e.key === ']') rotY += 0.1;
        else if (e.key === '-') height = Math.max(0.3, height - (e.shiftKey ? 0.005 : 0.02));
        else if (e.key === '=' || e.key === '+') height += (e.shiftKey ? 0.005 : 0.02);
        // Round finer than the smallest step so fine (Shift) increments survive.
        pos = pos.map((v) => Math.round(v * 100) / 100); rotY = Math.round(rotY * 100) / 100; height = Math.round(height * 1000) / 1000;
        const next = { pos, rotY, height };
        saveNpc(npc.slug, next); // autosave straight to roster.json (single source)
        return next;
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [PLACE, selected, npc.slug, npc.avatarId]);

  // Click behavior: select for placement in edit mode, else engage the chat.
  const onSelect = PLACE ? () => setNpcEdit({ slug: npc.slug }) : (engageable ? () => openChat({ avatarId: npc.avatarId, name: npc.name }) : null);

  const near = tier === 'near';
  const showLabel = PLACE || tier !== 'far';
  const labelText = PLACE ? `${npc.name}${selected ? '  ◄ moving' : ''}`
    : (near && engageable ? `🎙 ${npc.name?.split(' ')[0] || 'talk'} — tap to talk` : npc.name);

  return (
    <>
      <AnimatedResident
        meshUrl={npc.rigged}
        clipUrls={[npc.walk, npc.idle].filter(Boolean)}
        play="idle"
        avatarId={npc.avatarId}
        position={xf.pos}
        rotationY={xf.rotY}
        height={xf.height}
        attentionRadius={PLACE ? null : (npc.attentionRadius ?? 2.4)}
        midRadius={npc.midRadius ?? 9}
        phaseOffset={phaseOffset}
        rate={rate}
        onTier={onTier}
        onSelect={onSelect}
        onHover={onHover}
      />
      {showLabel && (
        <Html
          position={[xf.pos[0], (xf.height || 1.8) + 0.35, xf.pos[2]]}
          center distanceFactor={9} occlude={false}
          style={{ pointerEvents: 'none' }}
        >
          <div style={labelStyle(selected || (near && !PLACE))}>{labelText}</div>
        </Html>
      )}
    </>
  );
}
